// Line Items — the signed approval of a document (convert.sign.steps): the
// AUTHORISATION section, drawn as the PDF prints it (document-pdf.js): a box
// per step — the label and SIGNED / NOT SIGNED on its top line, the mark in
// a white panel, the name, the role and the moment, the place, the account.
// An unsigned box the reader may sign IS the pad: draw in the white panel,
// then choose the name (the Assigned Resource, a Director) and Sign.
//
// A mark is DRAWN on the canvas, PASTED (Ctrl+V over the pad) or ATTACHED
// as an image file; it becomes a PNG data URI. An image is cut down to its
// ink (the paper made transparent: inkOnly) and placed on the pad at the
// pad's own size, so an attached mark is never bigger than a drawn one; the
// signature then says it was attached (on the box, the PDF and History). Signing stamps the browser
// clock and — if the person allows it — the device's coordinates, asked at
// the moment of signing; a refusal signs without a place. To name the
// nearest town, the coordinates ALONE (rounded) go to a keyless reverse
// geocoder; if that fails the coordinates stand on their own.
//
// The name is required: a mark without one is not evidence of anything.
// This is not a cryptographic signature — an image, a name, a clock reading
// and a coordinate, like ink on a printed page.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { placeLine, mapsUrl } from '@modules/database/line-items/frontend/document-pdf';
import { inkOnly } from '@modules/database/line-items/frontend/ink';

const MAX_W = 900;   // the stored PNG is capped so it cannot bloat
const MAX_FILE = 15 * 1024 * 1024;   // a phone photo is fine; anything larger is not a signature
const MAX_PNG = 380000;              // under the server's 400 000-character cap



const GEOCODER = 'https://api.bigdatacloud.net/data/reverse-geocode-client';
async function placeOf(lat, lon) {
  try {
    const ctl = new AbortController(); const bail = setTimeout(() => ctl.abort(), 3000);
    const r = await fetch(`${GEOCODER}?latitude=${lat}&longitude=${lon}&localityLanguage=en`, { signal: ctl.signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
    clearTimeout(bail);
    if (!r.ok) return {};
    const d = await r.json();
    return {
      town: String(d.city || d.locality || d.principalSubdivision || '').trim() || undefined,
      postcode: String(d.postcode || '').trim() || undefined,
      country: String(d.countryName || '').trim() || undefined,
    };
  } catch { return {}; }
}
// A pending permission prompt is a human reading it: give it time, and
// look again when the clock ran out just as it was answered.
const permissionState = async () => {
  try { return navigator.permissions?.query ? (await navigator.permissions.query({ name: 'geolocation' })).state : 'unknown'; } catch { return 'unknown'; }
};
// → { lat, lon, acc } | { missing: 'denied' | 'unavailable' | 'timeout' | 'unsupported' }
// — when there is no place, WHY is kept with the signature (placeText says it).
const MISSING_BY_CODE = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };
const getPosition = (timeout) => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({ missing: 'unsupported' });
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: +pos.coords.latitude.toFixed(5), lon: +pos.coords.longitude.toFixed(5), acc: Math.round(pos.coords.accuracy) }),
    (e) => resolve({ missing: MISSING_BY_CODE[e && e.code] || 'unavailable' }), { enableHighAccuracy: false, timeout, maximumAge: 300000 });
});
async function whereAmI() {
  const state = await permissionState();
  if (state === 'denied') return { missing: 'denied' };
  const first = await getPosition(state === 'granted' ? 10000 : 30000);
  if (first.lat != null) return first;
  if (first.missing !== 'denied' && await permissionState() === 'granted') return getPosition(10000);
  return first;
}
// Said BEFORE signing, so the signer can still allow it.
const BLOCKED_HINT = 'Location is blocked for this site, so your signature will show "location not captured". To record it: click the icon left of the address bar → Site settings → Location → Allow, then reload. On a computer, Windows/Mac location services must be on too.';

const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
// A step moved past with no lines, in words (screen and PDF alike).
export const passedText = (p) => `No lines to sign for — moved on${p.toStatus ? ` to ${p.toStatus}` : ''} by ${p.name || 'someone'}${fmtWhen(p.when) ? ` — ${fmtWhen(p.when)}` : ''}.`;
// Who signs a step, in words.
export const signerText = (step) => (step.editors && step.field ? `naming the ${step.field}` : [step.field ? `the ${step.field}` : '', step.groups && step.groups.length ? step.groups.join(' / ') : ''].filter(Boolean).join(' or '));

// The AUTHORISATION. onSign(step, signature, extra) is awaited (a refusal
// shows in its box); onClear(step) — offered to the account that signed.
// passed: { [stepId]: { name, when, fromStatus, toStatus } } — a step the
// work item moved past unsigned because it had no lines (sign.emptyFree):
// its box says who moved it on, and when.
// numberFor(step) → { label, value } | null: a field the step fills as it
// signs (the order's number when the Director's signature makes it the order).
export function Authorisation({ steps, signatures, passed, me, busy, onSign, onClear, numberFor, convertTitle = '' }) {
  if (!steps || !steps.length) return null;
  return (
    <section className="li-auth" aria-label="Authorisation">
      <div className="li-auth-h">Authorisation</div>
      <div className="li-auth-grid">
        {steps.map((step, i) => {
          const s = signatures && signatures[step.id];
          const p = !s && passed && passed[step.id];
          const waitingFor = !s && !step.independent && steps.slice(0, i).find((x) => !(signatures && signatures[x.id]));
          return (
            <div key={step.id} className={`li-auth-box${s ? ' li-auth-signed' : ''}`}>
              <div className="li-auth-top">
                <span className="li-auth-label">{step.label}</span>
                <span className={`li-auth-state${s ? ' li-auth-ok' : ''}`}>{s ? 'Signed' : p ? 'Not needed — no lines' : 'Not signed'}</span>
              </div>
              {p && <div className="li-auth-meta li-auth-passed">{passedText(p)}</div>}
              {s ? <SignedBox step={step} s={s} busy={busy} onClear={onClear} />
                : step.canSign ? <PadBox step={step} me={me} onSign={onSign} number={numberFor ? numberFor(step) : null} convertTitle={convertTitle} />
                  : (
                    <>
                      <div className="li-auth-panel li-auth-panel-empty" aria-hidden="true" />
                      <div className="li-auth-meta">{waitingFor ? `Signed after the ${waitingFor.label}.` : step.why || `Signed by ${signerText(step)}.`}</div>
                    </>
                  )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SignedBox({ step, s, busy, onClear }) {
  const map = mapsUrl(s.where);
  return (
    <>
      <div className="li-auth-panel"><img src={s.image} alt={`${step.label} signature of ${s.name}`} /></div>
      <div className="li-auth-name">{s.name}</div>
      <div className="li-auth-meta">{step.role}{fmtWhen(s.when) ? ` — ${fmtWhen(s.when)}` : ''}</div>
      {s.attached && <div className="li-auth-meta li-auth-attached" title="The mark is an image attached by the signer, not drawn on the pad">Signed with an attached image</div>}
      <div className="li-auth-meta">{map ? <a href={map} target="_blank" rel="noopener noreferrer" title="Open where this was signed in Google Maps">{placeLine(s.where)}</a> : placeLine(s.where)}</div>
      <div className="li-auth-foot">
        {s.accountId && <span className="li-auth-acct" title="The account that signed">Account {s.accountId}{s.displayName && s.displayName !== s.name ? ` · ${s.displayName}` : ''}</span>}
        {step.canClear && <button type="button" className="li-auth-link" disabled={busy} onClick={() => onClear(step)} title="Remove your signature">Clear signature</button>}
      </div>
    </>
  );
}

// The pad, in the box: draw (finger, stylus or mouse), then choose the name
// and Sign. The place is asked for at the moment of signing.
function PadBox({ step, me, onSign, number, convertTitle }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [dirty, setDirty] = useState(false);
  const [attached, setAttached] = useState(false);   // the mark is an attached image
  const fileRef = useRef(null);
  // A `self` step (the Director) signs in the signed-in account's own name.
  const names = step.self ? [] : step.candidates || [];
  const [name, setName] = useState(() => {
    if (step.self) return me?.displayName || '';
    const own = names.find((n) => n.trim().toLowerCase() === String(me?.displayName || '').trim().toLowerCase());
    return own || (names.length === 1 ? names[0] : '');
  });
  const [num, setNum] = useState(number ? number.value || '' : '');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState('');
  const [err, setErr] = useState('');
  const [blocked, setBlocked] = useState(false);
  useEffect(() => { let on = true; permissionState().then((x) => { if (on) setBlocked(x === 'denied'); }); return () => { on = false; }; }, []);

  const fit = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const ratio = window.devicePixelRatio || 1; const r = c.getBoundingClientRect();
    if (!r.width) return;
    c.width = Math.round(r.width * ratio); c.height = Math.round(r.height * ratio);
    const ctx = c.getContext('2d'); ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0f172a';
    setDirty(false);
  }, []);
  useEffect(() => {
    fit();
    const c = canvasRef.current;
    const ro = c && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => { if (!drawing.current && !c.dataset.ink) fit(); }) : null;
    if (ro) ro.observe(c);
    return () => { if (ro) ro.disconnect(); };
  }, [fit]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const down = (e) => {
    if (busy) return;
    e.preventDefault();
    canvasRef.current.parentElement?.focus({ preventScroll: true });   // so Ctrl+V lands here (onPaste)
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true; last.current = pos(e);
    const ctx = canvasRef.current.getContext('2d'); const p = last.current;
    ctx.beginPath(); ctx.arc(p.x, p.y, 1, 0, Math.PI * 2); ctx.fillStyle = '#0f172a'; ctx.fill();
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d'); const p = pos(e); const a = last.current;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.quadraticCurveTo(a.x, a.y, (a.x + p.x) / 2, (a.y + p.y) / 2); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
    if (!dirty) { setDirty(true); canvasRef.current.dataset.ink = '1'; }
  };
  const up = () => { drawing.current = false; };
  const again = () => { const c = canvasRef.current; if (c) { c.getContext('2d').clearRect(0, 0, c.width, c.height); delete c.dataset.ink; } setDirty(false); setAttached(false); setErr(''); };

  // An attached (or pasted) image onto the pad: its ink only, fitted inside
  // the pad and centred — the pad's own pixels, so never bigger than a drawn mark.
  const placeImage = async (file) => {
    if (busy || !file) return;
    if (!/^image\//.test(file.type || '')) { setErr('Attach an image of the signature (PNG or JPG).'); return; }
    if (file.size > MAX_FILE) { setErr('That image is too large — attach one under 15 MB.'); return; }
    const url = URL.createObjectURL(file);
    try {
      const pic = await new Promise((ok, no) => { const im = new Image(); im.onload = () => ok(im); im.onerror = no; im.src = url; });
      const c = canvasRef.current; if (!c) return;
      const ratio = window.devicePixelRatio || 1; const m = 8 * ratio;
      const scale = Math.min((c.width - 2 * m) / pic.naturalWidth, (c.height - 2 * m) / pic.naturalHeight, 1);
      const w = Math.max(1, Math.round(pic.naturalWidth * scale)); const h = Math.max(1, Math.round(pic.naturalHeight * scale));
      const t = document.createElement('canvas'); t.width = w; t.height = h;
      const tctx = t.getContext('2d'); tctx.drawImage(pic, 0, 0, w, h);
      const data = tctx.getImageData(0, 0, w, h);
      if (inkOnly(data) < 30) { setErr('No signature found in that image — use dark ink on light paper.'); return; }
      tctx.putImageData(data, 0, 0);
      const ctx = c.getContext('2d');
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height);
      ctx.drawImage(t, Math.round((c.width - w) / 2), Math.round((c.height - h) / 2)); ctx.restore();
      c.dataset.ink = '1'; setDirty(true); setAttached(true); setErr('');
    } catch { setErr('That image could not be read — attach a PNG or JPG.'); }
    finally { URL.revokeObjectURL(url); }
  };
  const onPaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((x) => x.kind === 'file' && /^image\//.test(x.type));
    if (!item) return;
    e.preventDefault(); placeImage(item.getAsFile());
  };
  const attachLink = (
    <>
      <button type="button" className="li-auth-attach" disabled={busy} onClick={() => fileRef.current?.click()} title="Attach a picture or scan of your signature — or paste one (Ctrl+V) over the box">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
        Attach a signature image
      </button>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => { placeImage(e.target.files?.[0]); e.target.value = ''; }} />
    </>
  );

  // The mark trimmed to its ink, on white.
  const trimmed = () => {
    const c = canvasRef.current; const ctx = c.getContext('2d');
    const { width: w, height: h } = c; const px = ctx.getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (px[(y * w + x) * 4 + 3] > 0) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    if (x1 < 0) return c.toDataURL('image/png');
    const pad = 6 * (window.devicePixelRatio || 1);
    x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(w, x1 + pad); y1 = Math.min(h, y1 + pad);
    const scale = Math.min(1, MAX_W / (x1 - x0));
    const o = document.createElement('canvas'); o.width = Math.round((x1 - x0) * scale); o.height = Math.round((y1 - y0) * scale);
    const octx = o.getContext('2d'); octx.fillStyle = '#fff'; octx.fillRect(0, 0, o.width, o.height);
    octx.drawImage(c, x0, y0, x1 - x0, y1 - y0, 0, 0, o.width, o.height);
    return o.toDataURL('image/png');
  };
  // A searched name counts only when it is one of the step's people.
  const listed = !names.length || names.some((n) => n.trim().toLowerCase() === name.trim().toLowerCase());
  const sign = async () => {
    if (!dirty) { setErr('Sign in the white box first.'); return; }
    if (!name.trim()) { setErr(`Choose the ${step.label.toLowerCase()}.`); return; }
    if (!listed) { setErr(`“${name.trim()}” is not one of the ${step.label.toLowerCase()} names — pick one from the list as you type.`); return; }
    setBusy(true); setErr('');
    setStage(await permissionState() === 'granted' ? 'Finding where you are…' : 'Allow the location to stamp where you signed…');
    try {
      const w = await whereAmI();
      const where = w.lat != null ? { ...w, ...(await placeOf(w.lat, w.lon)) } : w;
      setStage('Signing…');
      const image = trimmed();
      if (image.length > MAX_PNG) throw new Error('That signature image is too detailed — crop it to the signature and attach it again.');
      await onSign(step, { image, name: name.trim(), when: new Date().toISOString(), where, ...(attached ? { attached: true } : {}) }, number ? { number: num } : null);
      return;
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Could not sign.'); }
    setBusy(false); setStage('');
  };

  return (
    <>
      <div className="li-auth-panel li-auth-pad" tabIndex={0} onPaste={onPaste} title="Draw here — or paste a signature image (Ctrl+V)">
        <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} aria-label={`Sign here as ${step.label}`} />
        {!dirty && <span className="li-auth-hint" aria-hidden="true">Sign here</span>}
      </div>
      {dirty ? (
        <div className="li-auth-form">
          <label className="li-auth-field"><span>{step.label}</span>
            {step.self ? <span className="li-auth-name">{name || 'You'}</span> : names.length ? (
              <>
                <input className="li-input" value={name} disabled={busy} list={`li-auth-names-${step.id}`} autoComplete="off"
                  onChange={(e) => { setName(e.target.value); setErr(''); }} placeholder={`Type to search the ${step.label.toLowerCase()}…`}
                  style={name.trim() && !listed ? { borderColor: '#dc2626' } : undefined} />
                <datalist id={`li-auth-names-${step.id}`}>{names.map((n) => <option key={n} value={n} />)}</datalist>
              </>
            ) : <input className="li-input" value={name} disabled={busy} onChange={(e) => { setName(e.target.value); setErr(''); }} placeholder="Full name" autoComplete="name" />}
          </label>
          {number && (
            <label className="li-auth-field"><span>{number.label}</span>
              <input className="li-input" value={num} disabled={busy} onChange={(e) => setNum(e.target.value)} placeholder="optional" />
            </label>
          )}
          <div className="li-auth-actions">
            <button type="button" className="li-btn li-btn-primary li-auth-signbtn" disabled={busy || !name.trim() || !listed} onClick={sign}>{busy ? 'Signing…' : step.converts ? 'Sign & approve' : 'Sign'}</button>
            <button type="button" className="li-auth-link" disabled={busy} onClick={again}>Start again</button>
            {attachLink}
            {stage && <span className="li-auth-meta">{stage}</span>}
          </div>
          {attached && <div className="li-auth-note">An attached image — the signature will say so.</div>}
          <div className="li-auth-note">{step.converts ? `Signing approves it: it becomes the ${convertTitle || 'approved document'} and its lines are final. ` : step.locks ? 'Signing makes the lines final. ' : ''}Your account, the time and — if you allow it — your location are recorded.</div>
        </div>
      ) : (
        <div className="li-auth-start">
          <div className="li-auth-meta">{step.self ? `Draw your signature above — you sign as ${name || 'yourself'}.` : names.length ? `Draw your signature above, then search for the ${step.label.toLowerCase()}.` : 'Draw your signature above.'}</div>
          <div className="li-auth-or"><span className="li-auth-meta">or</span>{attachLink}<span className="li-auth-meta li-auth-paste">or paste one (Ctrl+V) on the box</span></div>
        </div>
      )}
      {blocked && <div className="li-auth-note li-auth-blocked">{BLOCKED_HINT}</div>}
      {err && <div className="li-error">{err}</div>}
    </>
  );
}
